import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, Clock, StickyNote, ChevronRight } from 'lucide-react';
import { isRtl } from '../../i18n/direction';

/**
 * TherapyTodayCard — the topmost card on the therapy dashboard. Designed
 * for a therapist on a phone: tells them what their day looks like in one
 * glance and gives them the right single CTA. Three states:
 *
 *   1) Nothing on the calendar today → "Schedule a meeting" CTA
 *   2) Has meetings, none of them past → "Next at HH:MM with X" CTA
 *   3) Past meetings without notes → "Write notes" CTA (most-recent first)
 *
 * On desktop the same card is still useful as the "welcome" strip above
 * the KPIs, but it's tuned so the mobile flow is the cheapest path.
 */
export default function TherapyTodayCard({
  todayMeetings = [],
  pendingNotes = [],
  isMobile,
  isTablet,
  onScheduleNewMeeting,
  onOpenMeeting,
  onAddNoteForMeeting,
  userName,
}) {
  const { t, i18n } = useTranslation();
  const rtl = isRtl(i18n.language);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return t('therapy.dashboard.morningGreeting', 'Good morning');
    if (h < 18) return t('therapy.dashboard.afternoonGreeting', 'Good afternoon');
    return t('therapy.dashboard.eveningGreeting', 'Good evening');
  }, [t, i18n.language]);

  const now = Date.now();
  const nextMeeting = useMemo(() => {
    return todayMeetings.find((m) => {
      const d = new Date(m.date);
      if (Number.isNaN(d.getTime())) return false;
      if (!m.time) return d.getTime() > now;
      const [h, mm] = m.time.split(':').map(Number);
      const startMs = d.getTime() + (h * 60 + (mm || 0)) * 60 * 1000;
      return startMs > now;
    }) || null;
  }, [todayMeetings, now]);

  const oldestPending = pendingNotes[pendingNotes.length - 1] || null;

  let mode;
  let primaryAction = null;
  let supportingLines = [];

  if (oldestPending) {
    mode = 'notes';
    primaryAction = {
      label: t('therapy.dashboard.addNotes', 'Add notes'),
      icon: StickyNote,
      onClick: () => onAddNoteForMeeting?.(oldestPending),
    };
    supportingLines = [
      pendingNotes.length === 1
        ? t('therapy.dashboard.oneNoteOwed', 'One session is waiting for notes')
        : t('therapy.dashboard.manyNotesOwed', '{{count}} sessions are waiting for notes', { count: pendingNotes.length }),
    ];
  } else if (nextMeeting) {
    mode = 'next';
    primaryAction = {
      label: t('therapy.dashboard.openMeeting', 'Open'),
      icon: ChevronRight,
      onClick: () => onOpenMeeting?.(nextMeeting),
    };
    supportingLines = [
      t('therapy.dashboard.youHaveMeetingsToday', {
        count: todayMeetings.length,
        defaultValue: 'You have {{count}} session today',
      }),
      t('therapy.dashboard.nextUpAt', 'Next at {{time}}', {
        time: nextMeeting.time || '',
      }) + (nextMeeting.studentName ? ` · ${nextMeeting.studentName}` : ''),
    ];
  } else if (todayMeetings.length > 0) {
    // Everything today already finished and was already written up.
    mode = 'allDone';
    primaryAction = {
      label: t('therapy.quickActions.newMeeting', 'New meeting'),
      icon: CalendarPlus,
      onClick: () => onScheduleNewMeeting?.(),
    };
    supportingLines = [t('therapy.dashboard.allCaughtUp', 'You\'re all caught up for today')];
  } else {
    mode = 'empty';
    primaryAction = {
      label: t('therapy.quickActions.newMeeting', 'New meeting'),
      icon: CalendarPlus,
      onClick: () => onScheduleNewMeeting?.(),
    };
    supportingLines = [t('therapy.dashboard.nothingToday', 'Nothing on your calendar today')];
  }

  const Icon = primaryAction.icon;

  return (
    <div
      data-testid="therapy-today-card"
      data-mode={mode}
      style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: '14px',
        padding: isMobile ? '1rem' : '1.25rem 1.5rem',
        marginBottom: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        direction: rtl ? 'rtl' : 'ltr',
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: '1rem',
        flexDirection: isMobile ? 'column' : 'row',
        // Subtle accent stripe — color-coded by mode for one-glance triage
        borderInlineStart: `4px solid ${
          mode === 'notes' ? '#f59e0b'
            : mode === 'next' ? '#0ea5e9'
              : mode === 'allDone' ? '#22c55e'
                : 'var(--color-border)'
        }`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0,
          fontSize: '0.75rem',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          {greeting}{userName ? ` · ${userName}` : ''}
        </p>
        {supportingLines.map((line, i) => (
          <p
            key={i}
            style={{
              margin: i === 0 ? '0.25rem 0 0 0' : '0.125rem 0 0 0',
              fontSize: i === 0 ? '1.0625rem' : '0.8125rem',
              fontWeight: i === 0 ? 600 : 400,
              color: i === 0 ? 'var(--color-text-primary, #111827)' : 'var(--color-text-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
            }}
          >
            {i > 0 && <Clock size={12} />}
            {line}
          </p>
        ))}
      </div>

      <button
        data-testid="therapy-today-card-cta"
        onClick={primaryAction.onClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.625rem 1rem',
          borderRadius: '10px',
          border: 'none',
          backgroundColor: mode === 'notes' ? '#f59e0b'
            : mode === 'next' ? '#0ea5e9'
              : mode === 'allDone' ? '#22c55e'
                : 'var(--color-primary, #0ea5e9)',
          color: !['notes', 'next', 'allDone'].includes(mode) ? 'var(--color-on-primary, #fff)' : '#fff',
          cursor: 'pointer',
          fontSize: '0.9375rem',
          fontWeight: 600,
          minHeight: '48px',
          width: isMobile ? '100%' : 'auto',
          justifyContent: 'center',
        }}
      >
        <Icon size={18} />
        {primaryAction.label}
      </button>
    </div>
  );
}
